/**
 * 📄 HOOK GESTION PAIEMENTS FOURNISSEURS LOGISTICS
 * ==================================================
 * Hook custom pour la gestion des règlements fournisseurs (décaissements)
 *
 * Workflow:
 * 1. Afficher l'historique des paiements d'une facture fournisseur
 * 2. Enregistrer un nouveau paiement (partiel ou complet)
 * 3. Suppression d'un paiement avec son droit dédié
 * 4. Recalcul du balance_due et payment_status dans la transaction backend
 *
 * Backend:
 * - GET /api/logistics/supplier-payments?supplier_bill_id=X
 * - POST /api/logistics/supplier-payments (création)
 * - DELETE /api/logistics/supplier-payments/:id (suppression)
 *
 * Les anciens triggers DB restent compatibles avec le service partagé.
 *
 * @see routes/logistics/supplier-payments.js
 * @see src/gestion-logistics/types/payment.types.ts
 */

import { useState, useCallback } from 'react';
import apiClient from '../../api/apiClient';
import { message } from 'antd';
import API_CONFIG from '../../config/api';
import { SupplierPayment, SupplierPaymentFormData } from '../types/payment.types';
import { useSelection } from '../../SelectionContext';

export const useSupplierPayments = () => {
  const [loading, setLoading] = useState(false);
  const [supplierPayments, setSupplierPayments] = useState<SupplierPayment[]>([]);
  const [currentPayment, setCurrentPayment] = useState<SupplierPayment | null>(null);

  const { selectedCompany } = useSelection();

  /**
   * Récupérer tous les paiements d'une facture fournisseur spécifique
   * @param supplierBillId - ID de la facture fournisseur
   */
  const fetchPaymentsBySupplierBill = useCallback(async (supplierBillId: number) => {
    setLoading(true);
    try {
      const response = await apiClient.get(
        `${API_CONFIG.API_URL}/logistics/supplier-payments`,
        {
          params: { supplier_bill_id: supplierBillId },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        }
      );

      if (response.data.success) {
        const paymentsList = response.data.data || [];
        setSupplierPayments(paymentsList);
        console.log('✅ [useSupplierPayments] Paiements chargés:', paymentsList.length);
        return paymentsList;
      } else {
        message.error(response.data.message || 'Erreur lors du chargement des paiements');
        return [];
      }
    } catch (error: any) {
      console.error('❌ [useSupplierPayments] Erreur fetchPaymentsBySupplierBill:', error);
      message.error(error.response?.data?.message || 'Erreur de connexion au serveur');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Récupérer les détails d'un paiement spécifique
   * @param paymentId - ID du paiement
   */
  const fetchPaymentById = useCallback(async (paymentId: number): Promise<SupplierPayment | null> => {
    setLoading(true);
    try {
      const response = await apiClient.get(
        `${API_CONFIG.API_URL}/logistics/supplier-payments/${paymentId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        }
      );

      if (response.data.success) {
        const payment = response.data.data;
        setCurrentPayment(payment);
        console.log('✅ [useSupplierPayments] Paiement chargé:', payment.payment_reference);
        return payment;
      } else {
        message.error(response.data.message || 'Erreur lors du chargement du paiement');
        return null;
      }
    } catch (error: any) {
      console.error('❌ [useSupplierPayments] Erreur fetchPaymentById:', error);
      message.error(error.response?.data?.message || 'Erreur de connexion');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Enregistrer un nouveau paiement fournisseur
   *
   * Validations automatiques backend:
   * - Montant ne doit pas dépasser balance_due
   * - Facture doit exister et appartenir à la company
   * - Transaction atomique avec mise à jour auto via trigger
   *
   * @param data - Données du paiement
   */
  const recordPayment = useCallback(async (
    data: SupplierPaymentFormData
  ): Promise<SupplierPayment | null> => {
    setLoading(true);
    try {
      console.log('💰 [useSupplierPayments] Enregistrement paiement:', data);

      const response = await apiClient.post(
        `${API_CONFIG.API_URL}/logistics/supplier-payments`,
        {
          ...data,
          company_id: selectedCompany, // SuperAdmin context
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        const newPayment = response.data.data;
        message.success(
          response.data.message || 'Règlement enregistré avec succès'
        );

        // Ajouter le nouveau paiement à la liste
        setSupplierPayments((prev) => [newPayment, ...prev]);
        setCurrentPayment(newPayment);

        console.log('✅ [useSupplierPayments] Paiement créé:', newPayment.amount);
        return newPayment;
      } else {
        message.error(response.data.message || 'Erreur lors de l\'enregistrement');
        return null;
      }
    } catch (error: any) {
      console.error('❌ [useSupplierPayments] Erreur recordPayment:', error);
      message.error(
        error.response?.data?.message || 'Erreur lors de l\'enregistrement du paiement'
      );
      return null;
    } finally {
      setLoading(false);
    }
  }, [selectedCompany]);

  /**
   * Supprimer un paiement fournisseur
   * IMPORTANT: Recalcul automatique via trigger DB
   *
   * @param paymentId - ID du paiement à supprimer
   */
  const deletePayment = useCallback(async (paymentId: number): Promise<boolean> => {
    setLoading(true);
    try {
      console.log('🗑️ [useSupplierPayments] Suppression paiement:', paymentId);

      const response = await apiClient.delete(
        `${API_CONFIG.API_URL}/logistics/supplier-payments/${paymentId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        }
      );

      if (response.data.success) {
        message.success('Règlement supprimé avec succès');

        // Retirer le paiement de la liste
        setSupplierPayments((prev) => prev.filter((payment) => payment.id !== paymentId));

        console.log('✅ [useSupplierPayments] Paiement supprimé (balance recalculé via trigger)');
        return true;
      } else {
        message.error(response.data.message || 'Erreur lors de la suppression');
        return false;
      }
    } catch (error: any) {
      console.error('❌ [useSupplierPayments] Erreur deletePayment:', error);
      message.error(error.response?.data?.message || 'Erreur lors de la suppression');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // État
    loading,
    supplierPayments,
    currentPayment,

    // Actions
    fetchPaymentsBySupplierBill,
    fetchPaymentById,
    recordPayment,
    deletePayment,
  };
};

export default useSupplierPayments;
